import {useState, useEffect} from 'react';
import {loginSchema} from '../../../lib/zodUtils';
import {useLoginMutation} from '../../../store/api/authApi';
import {useDispatch, useSelector} from 'react-redux';
import {setCredentials} from '../../../store/slices/authSlice';
import {RootState} from '../../../store/store';
import {jwtDecode} from 'jwt-decode';
import {
  useGetSubjectsQuery,
  useGetLanguagesQuery,
  useGetSchoolsQuery,
  useGetGradesQuery,
  useGetCategoriesQuery,
} from '../../../store/api/masterApi';
import {
  setSubjects,
  setLanguages,
  setSchools,
  setGrades,
  setCategories,
} from '../../../store/slices/masterSlice';
import {isTutor} from '../../../lib/userUtil';
import { useNavigate } from 'react-router-dom';

interface ValidationErrors {
  email?: string[];
  password?: string[];
}

interface DecodedToken {
  id: number;
  email: string;
  userType: string;
  iat: number;
  exp: number;
  tutor?: {
    id: number;
    firstName: string;
    lastName: string;
    bio?: string;
    approval: boolean;
    reason?: string | null;
    userId: number;
  };
  student?: {
    id: number;
    firstName: string;
    lastName: string;
    approval?: boolean;
    userId: number;
  };
}

export const useLogin = () => {
  const [login, {isLoading: isLoginLoading, error: apiError}] = useLoginMutation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token, user } = useSelector((state: RootState) => state.auth);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<ValidationErrors>({});

  // Fetch master data using RTK Query hooks
  const {data: subjects, isLoading: isSubjectsLoading} = useGetSubjectsQuery();
  const {data: languages, isLoading: isLanguagesLoading} = useGetLanguagesQuery();
  const {data: schools, isLoading: isSchoolsLoading} = useGetSchoolsQuery();
  const {data: grades, isLoading: isGradesLoading} = useGetGradesQuery();
  const {data: categories, isLoading: isCategoriesLoading} = useGetCategoriesQuery();

  const isMasterDataLoading =
    isSubjectsLoading ||
    isLanguagesLoading ||
    isSchoolsLoading ||
    isGradesLoading ||
    isCategoriesLoading;

  // Check if user is already logged in with valid token
  useEffect(() => {
    if (token && user) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Date.now() / 1000;
        
        if (payload.exp > currentTime) {
          // Token is still valid, redirect to appropriate dashboard
          console.log('User already logged in, redirecting...');
          if (user.userType === 'tutor') {
            navigate('/TutorDashboard');
          } else if (user.userType === 'student') {
            navigate('/StudentDashboard');
          }
        }
      } catch (error) {
        console.error('Error checking token validity:', error);
      }
    }
  }, [token, user, navigate]);

  // Store master data in Redux as soon as it's available
  useEffect(() => {
    if (!isMasterDataLoading) {
      if (subjects) dispatch(setSubjects(subjects));
      if (languages) dispatch(setLanguages(languages));
      if (schools) dispatch(setSchools(schools));
      if (grades) dispatch(setGrades(grades));
      if (categories) dispatch(setCategories(categories));
    }
  }, [subjects, languages, schools, grades, categories, isMasterDataLoading, dispatch]);

  const handleSubmit = async (usertype: string)  => {
    setErrors({});
    const validationResult = loginSchema.safeParse({email, password});

    if (!validationResult.success) {
      const formattedErrors = validationResult.error.flatten().fieldErrors;
      setErrors(formattedErrors);
      console.error('Validation Errors:', formattedErrors);
      return;
    }

    try {
      const response = await login({email, password, usertype}).unwrap();
      const token = response.data.access_token;
      const decoded: DecodedToken = jwtDecode(token);

      const isTutorUser = isTutor(decoded.userType);
      const userProfile = isTutorUser ? decoded.tutor : decoded.student;

      const userId = userProfile?.id;
      const isTutorApproved =  userProfile?.approval;
      const userLastName = userProfile?.lastName;
      const userFirstName = userProfile?.firstName;
      
      // Check if tutor is approved before allowing login
      if (isTutorUser && !isTutorApproved) {
        setErrors({
          email: ['Your tutor account is pending approval. Please wait for admin approval before logging in.']
        });
        return;
      }
      
      // Save to Redux store
      dispatch(
        setCredentials({
          token,
          user: {
            id: userId || 0,
            email: decoded.email,
            userType: decoded.userType,
            isTutorApproved: isTutorApproved || false,
            lastName:userLastName ||"",
            firstName: userFirstName ||""
          },
        }),
      );
      console.log('Login Successful:', response);
      if (!isTutor(decoded.userType)) {
        navigate('/StudentDashboard');
      } else {
        navigate('/TutorDashboard');
      }
    } catch (err) {
      console.log('Login Failed:', err);
    }
  };

  const registerStudent = () => {
    navigate('/StudentSignUpScreen');
  };

  const registerTutor = () => {
  navigate('/tutor-onboarding');
  };

  const errorMessage = apiError
    ? 'data' in apiError
      ? (apiError.data as any)?.message || 'Login failed'
      : 'Network error'
    : null;

  return {
    email,
    password,
    errors,
    setEmail,
    setPassword,
    handleSubmit,
    registerStudent,
    registerTutor,
    isLoginLoading,
    errorMessage,
    isMasterDataLoading,
  };
};
